import CloseIcon from '@mui/icons-material/Close';
import { Button, Grid, IconButton, InputBase } from '@mui/material';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { buttonStyle } from '../TransitionStyles';

const programKey = (program) =>
  program?.isNewProgram
    ? `new:${String(program.programName).trim().toLowerCase()}`
    : `${program?.isGeneralOption ? 'general' : 'company'}:${program?.programId ?? ''}`;

const toDto = (option) => ({
  programId: Number(option.value),
  isGeneralOption: Boolean(option.isGeneralOption),
  isNewProgram: false,
  programName: '',
});

function AddProgramInput({ onAdd, onCancel }) {
  const [name, setName] = useState('');

  return (
    <Button
      variant="contained"
      component="div"
      disableRipple
      disableFocusRipple
      disableTouchRipple
      disableElevation
      sx={{ ...buttonStyle, mt: 1, position: 'relative', height: 30, minHeight: 30 }}
    >
      <InputBase
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onAdd(name);
          }
        }}
        sx={{ width: '10ch', fontSize: 'inherit', '& input': { p: 0, fontSize: 'inherit' } }}
      />
      <IconButton
        size="small"
        color="inherit"
        aria-label="Cancel add program"
        onMouseDown={(event) => event.preventDefault()}
        onClick={onCancel}
        sx={{ position: 'absolute', top: 1, right: 1, p: 0.1 }}
      >
        <CloseIcon sx={{ fontSize: 13 }} />
      </IconButton>
    </Button>
  );
}

AddProgramInput.propTypes = {
  onAdd: PropTypes.func,
  onCancel: PropTypes.func,
};

FormFacilityProgramsField.propTypes = {
  fieldName: PropTypes.string,
  dbProgramOptions: PropTypes.array,
  isEditable: PropTypes.bool,
};

export default function FormFacilityProgramsField({ fieldName, dbProgramOptions, isEditable }) {
  const { control } = useFormContext();
  const [isAddingProgram, setIsAddingProgram] = useState(false);
  const { field } = useController({ name: fieldName, control });
  const selectedPrograms = Array.isArray(field.value) ? field.value : [];
  const selectedProgramKeys = new Set(selectedPrograms.map(programKey));
  const isCanceled = useSelector((state) => state.transitions?.data?.isCanceled);

  const dbOptions = dbProgramOptions.map((option) => {
    const program = toDto(option);
    return { label: option.label, key: programKey(program), program };
  });
  const dbOptionKeys = new Set(dbOptions.map((option) => option.key));

  const unsavedOptions = selectedPrograms
    .filter((program) => program?.isNewProgram && program?.programName && !dbOptionKeys.has(programKey(program)))
    .map((program) => ({ label: String(program.programName).trim(), key: programKey(program), program }));

  const options = [...dbOptions, ...unsavedOptions];

  const toggleProgram = (program) => {
    const key = programKey(program);
    const updatedSelectedPrograms = selectedProgramKeys.has(key)
      ? selectedPrograms.filter((currentProgram) => programKey(currentProgram) !== key)
      : [...selectedPrograms, program];

    field.onChange(updatedSelectedPrograms);
  };

  const addProgram = (name) => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    const existingProgram = dbProgramOptions.find(
      (option) => option.label.trim().toLowerCase() === trimmedName.toLowerCase()
    );

    const program = existingProgram
      ? toDto(existingProgram)
      : {
          programId: 0,
          isGeneralOption: false,
          isNewProgram: true,
          programName: trimmedName,
        };

    if (!selectedProgramKeys.has(programKey(program))) {
      toggleProgram(program);
    }

    setIsAddingProgram(false);
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={() => (
        <Grid container spacing={1}>
          {options.map((option) => (
            <Grid item key={option.key}>
              <Button
                disabled={!isEditable || isCanceled}
                variant={selectedProgramKeys.has(option.key) ? 'contained' : 'outlined'}
                onClick={() => toggleProgram(option.program)}
                sx={{ ...buttonStyle, mt: 1 }}
              >
                {option.label}
              </Button>
            </Grid>
          ))}

          {isEditable && (
            <Grid item>
              {isAddingProgram ? (
                <AddProgramInput onAdd={addProgram} onCancel={() => setIsAddingProgram(false)} />
              ) : (
                <Button
                  variant="outlined"
                  disabled={isCanceled}
                  onClick={() => setIsAddingProgram(true)}
                  sx={{ ...buttonStyle, mt: 1, borderStyle: 'dashed' }}
                >
                  + Add New
                </Button>
              )}
            </Grid>
          )}
        </Grid>
      )}
    />
  );
}
